import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";

const API = process.env.REACT_APP_API_URL || "";

const STATUS_MAP = {
  Pending:       { bg: "#fef3c7", color: "#92400e" },
  Approved:      { bg: "#d1fae5", color: "#065f46" },
  Confirmed:     { bg: "#dbeafe", color: "#1e40af" },
  CheckedIn:     { bg: "#d1fae5", color: "#065f46" },
  CheckedOut:    { bg: "#f3f4f6", color: "#374151" },
  PendingCancel: { bg: "#f3e8ff", color: "#6b21a8" },
  Cancelled:     { bg: "#fee2e2", color: "#991b1b" },
};

function StatusBadge({ status, lang }) {
  const s = STATUS_MAP[status] ?? { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.color,
      whiteSpace: "nowrap",
    }}>
      {t(lang, status) || status}
    </span>
  );
}

const Profile = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [editMode,     setEditMode]     = useState(false);
  const [phoneEdit,    setPhoneEdit]    = useState(false);
  const [phoneDraft,   setPhoneDraft]   = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [bookings,     setBookings]     = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);

  const [profile, setProfile] = useState({
    firstname: "", lastname: "", birthday: "", gender: "",
    address: "", country: "", email: "", phonenumber: "",
    lastLogin: { date: "", ip: "", browser: "" },
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) { localStorage.clear(); navigate("/login"); return; }
        if (!res.ok) throw new Error();
        setProfile(await res.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate, token]);

  useEffect(() => {
    if (!token) return;
    const fetchBookings = async () => {
      try {
        const res  = await fetch(`${API}/api/bookings/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setBookings(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingBooks(false);
      }
    };
    fetchBookings();
  }, [token]);

  const handleChange = (e) => setProfile({ ...profile, [e.target.name]: e.target.value });

  const handleSave = async () => {
    if (!token) { navigate("/login"); return; }
    try {
      /* ส่งเฉพาะ field ที่แก้ไขได้ */
      const { firstname, lastname, birthday, gender, address, country, phonenumber } = profile;
      const res = await fetch(`${API}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ firstname, lastname, birthday, gender, address, country, phonenumber }),
      });
      if (!res.ok) throw new Error();
      alert("บันทึกข้อมูลสำเร็จ ✅");
      setEditMode(false);
    } catch {
      alert("บันทึกไม่สำเร็จ ❌");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(URL.createObjectURL(file));   // แสดง preview ทันที
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const res = await fetch(`${API}/api/profile/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProfile(p => ({ ...p, avatar: data.avatar }));
    } catch {
      alert("อัปโหลดรูปไม่สำเร็จ ❌");
      setProfileImage(null);
    }
  };

  const handlePhoneSave = async () => {
    try {
      const res = await fetch(`${API}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phonenumber: phoneDraft.trim() }),
      });
      if (!res.ok) throw new Error();
      setProfile(p => ({ ...p, phonenumber: phoneDraft.trim() }));
      setPhoneEdit(false);
    } catch {
      alert("บันทึกเบอร์โทรไม่สำเร็จ ❌");
    }
  };

  /* รูปโปรไฟล์: ไฟล์ที่เพิ่งเลือก > avatar ใน DB (รองรับทั้ง URL จาก Google และไฟล์ใน /uploads) */
  const avatarSrc = profileImage
    || (profile.avatar
        ? (profile.avatar.startsWith("http") ? profile.avatar : `${API}/uploads/${profile.avatar}`)
        : null);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" }) : "—";
  const fmt = (n) => Number(n || 0).toLocaleString();

  if (loading) return <p style={{ padding: 40 }}>{t(lang, "loading")}</p>;

  const inputStyle = (active) => ({
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: "1px solid #e5e7eb", fontSize: 13, color: "#111827",
    background: active ? "#fff" : "#f9fafb",
    boxSizing: "border-box", outline: "none",
    fontFamily: "'Noto Sans Thai', sans-serif",
  });

  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: "#9ca3af",
    letterSpacing: ".4px", textTransform: "uppercase", display: "block", marginBottom: 4,
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f0f0f0", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <style>{`
        @media (max-width: 700px) {
          .profile-two-col { flex-direction: column !important; }
          .profile-divider  { width: 100% !important; height: 1px !important; align-self: auto !important; }
          .profile-left     { flex: none !important; padding-right: 0 !important; width: 100% !important; }
          .profile-right    { flex: none !important; width: 100% !important; padding-left: 0 !important; padding-top: 24px !important; }
        }
        @media (max-width: 480px) {
          .profile-page-inner { padding: 16px 12px 40px !important; }
          .pf-card            { padding: 20px 16px !important; }
          .profile-avatar-row { flex-wrap: wrap !important; gap: 14px !important; }
          .profile-avatar-row .profile-avatar-img { width: 70px !important; height: 70px !important; }
          .profile-avatar-row .profile-edit-wrap  { margin-left: auto !important; }
          .profile-fields     { grid-template-columns: 1fr !important; }
          .profile-fields .profile-name-inputs { flex-direction: column !important; }
          .profile-booking-table th,
          .profile-booking-table td { padding: 10px 12px !important; }
        }
      `}</style>
      <Navbar />

      <div className="profile-page-inner" style={{ maxWidth: 1020, margin: "0 auto", padding: "32px 20px 60px" }}>

        {/* ── PROFILE CARD ── */}
        <div className="pf-card" style={{
          background: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          borderRadius: 18,
          boxShadow: "0 8px 32px rgba(0,0,0,0.10), 0 1px 0 rgba(255,255,255,0.8) inset",
          border: "1px solid rgba(255,255,255,0.75)",
          padding: "32px 36px",
          marginBottom: 24,
        }}>
          <h2 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>👤</span> {t(lang, "profileUser")}
          </h2>

          {/* 2-column: LEFT = avatar + fields, RIGHT = security */}
          <div className="profile-two-col" style={{ display: "flex", gap: 0, alignItems: "flex-start" }}>

            {/* ── LEFT: Avatar + name + fields ── */}
            <div className="profile-left" style={{ flex: "0 0 55%", paddingRight: 36 }}>

              {/* Avatar row */}
              <div className="profile-avatar-row" style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 24 }}>
                <div style={{ flexShrink: 0 }}>
                  {avatarSrc
                    ? <img src={avatarSrc} alt="profile" className="profile-avatar-img"
                        style={{ width: 90, height: 90, borderRadius: "50%", objectFit: "cover", border: "3px solid #e5e7eb" }} />
                    : <FaUserCircle size={90} color="#d1d5db" />
                  }
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>
                    {profile.firstname || profile.lastname
                      ? `${profile.firstname} ${profile.lastname}`
                      : t(lang, "defaultUser")}
                  </div>
                  <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>{profile.email}</div>
                  <label style={{
                    marginTop: 6, display: "inline-block",
                    fontSize: 12, color: "#1d4ed8", cursor: "pointer",
                    fontWeight: 600, textDecoration: "underline",
                  }}>
                    {t(lang, "changePhoto")}
                    <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                  </label>
                </div>
                {/* Edit button — top-right of this column */}
                <div className="profile-edit-wrap" style={{ marginLeft: "auto" }}>
                  <button
                    onClick={() => editMode ? handleSave() : setEditMode(true)}
                    style={{
                      padding: "7px 20px", borderRadius: 9, border: "none",
                      background: editMode ? "#16a34a" : "#111827",
                      color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
                    }}
                  >
                    {editMode ? t(lang, "save") : t(lang, "editBtn")}
                  </button>
                </div>
              </div>

              {/* Fields grid */}
              <div className="profile-fields" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>{t(lang, "fullName")}</label>
                  <div className="profile-name-inputs" style={{ display: "flex", gap: 8 }}>
                    <input name="firstname" value={profile.firstname || ""} onChange={handleChange}
                      disabled={!editMode} placeholder={t(lang, "firstPh")} style={inputStyle(editMode)} />
                    <input name="lastname"  value={profile.lastname  || ""} onChange={handleChange}
                      disabled={!editMode} placeholder={t(lang, "lastPh")} style={inputStyle(editMode)} />
                  </div>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>{t(lang, "addressLabel")}</label>
                  <input name="address" value={profile.address || ""} onChange={handleChange}
                    disabled={!editMode} placeholder={t(lang, "addressPh")} style={inputStyle(editMode)} />
                </div>

                <div>
                  <label style={labelStyle}>{t(lang, "birthdayLabel")}</label>
                  <input type="date" name="birthday" value={(profile.birthday || "").slice(0, 10)} onChange={handleChange}
                    disabled={!editMode} style={inputStyle(editMode)} />
                </div>

                <div>
                  <label style={labelStyle}>{t(lang, "genderLabel")}</label>
                  <select name="gender" value={profile.gender || ""} onChange={handleChange}
                    disabled={!editMode} style={inputStyle(editMode)}>
                    <option value="">{t(lang, "genderSelect")}</option>
                    <option value="male">{t(lang, "male")}</option>
                    <option value="female">{t(lang, "female")}</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>{t(lang, "countryLabel")}</label>
                  <input name="country" value={profile.country || ""} onChange={handleChange}
                    disabled={!editMode} placeholder={t(lang, "countryPh")} style={inputStyle(editMode)} />
                </div>

                <div>
                  <label style={labelStyle}>{t(lang, "phoneNumLabel")}</label>
                  <input name="phonenumber" value={profile.phonenumber || ""} onChange={handleChange}
                    disabled={!editMode} placeholder="0xx-xxx-xxxx" style={inputStyle(editMode)} />
                </div>

              </div>
            </div>

            {/* Vertical divider */}
            <div className="profile-divider" style={{ width: 1, background: "#e5e7eb", alignSelf: "stretch", flexShrink: 0 }} />

            {/* ── RIGHT: Security ── */}
            <div className="profile-right" style={{ flex: 1, paddingLeft: 36 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 16 }}>
                {t(lang, "accountSecurity")}
              </div>

              {[
                { icon: "✉️", label: t(lang, "emailLabel"),   value: profile.email,                         btn: null },
                { icon: "📱", label: t(lang, "mobileLabel"),
                  value: phoneEdit
                    ? <input autoFocus value={phoneDraft} onChange={e => setPhoneDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handlePhoneSave(); if (e.key === "Escape") setPhoneEdit(false); }}
                        placeholder="0xx-xxx-xxxx"
                        style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db",
                                 fontSize: 13, outline: "none", fontFamily: "inherit", width: 160 }} />
                    : (profile.phonenumber || t(lang, "notSpecified")),
                  btn: phoneEdit ? t(lang, "save") : t(lang, "editBtn"),
                  btnColor: phoneEdit ? "#16a34a" : undefined,
                  action: phoneEdit
                    ? handlePhoneSave
                    : () => { setPhoneDraft(profile.phonenumber || ""); setPhoneEdit(true); } },
                { icon: "🔒", label: t(lang, "passwordLabel"), value: t(lang, "changePwdEmail"),
                  btn: t(lang, "changeBtn"), action: () => navigate("/forgot-password") },
              ].map((row, i) => (
                <div key={i} className="profile-sec-row" style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "14px 0", borderBottom: "1px solid #f3f4f6",
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 14 }}>{row.icon}</span>{row.label}
                    </div>
                    <div style={{ fontSize: 13, color: "#111827", marginTop: 3 }}>{row.value}</div>
                  </div>
                  {row.btn && (
                    <button onClick={row.action} className="profile-sec-btn" style={{
                      padding: "7px 18px", borderRadius: 9,
                      border: "none", background: row.btnColor || "#111827",
                      color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
                      whiteSpace: "nowrap", flexShrink: 0, marginLeft: "auto",
                    }}>
                      {row.btn}
                    </button>
                  )}
                </div>
              ))}

              {/* Last login */}
              <div style={{ marginTop: 20, padding: "14px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
                  {t(lang, "lastLogin")}
                </div>
                {profile.lastLogin?.date ? (
                  <>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {new Date(profile.lastLogin.date).toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                      {profile.lastLogin.browser} • {profile.lastLogin.ip}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>วันนี้ เวลา 18:34, Chrome 198.123.23.23</div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* ── BOOKING HISTORY ── */}
        <div style={{
          background: "#fff", borderRadius: 18,
          boxShadow: "0 2px 12px rgba(0,0,0,.07)",
          border: "1px solid #ececec",
          overflow: "hidden",
        }}>
          <div style={{ padding: "18px 28px", borderBottom: "1px solid #f3f4f6" }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>🗂 {t(lang, "bookingHistoryTitle")}</h3>
          </div>

          {/* Scrollable table container */}
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 320 }}>
            {loadingBooks ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t(lang, "loading")}</div>
            ) : bookings.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t(lang, "noHistory")}</div>
            ) : (
              <table className="profile-booking-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                    {[t(lang,"colRoom"), t(lang,"colType"), t(lang,"colCheckIn"), t(lang,"colCheckOut"), t(lang,"colTotal"), t(lang,"colPaid"), t(lang,"colRemaining"), t(lang,"colStatus")].map((h) => (
                      <th key={h} style={{
                        padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280",
                        textAlign: "left", textTransform: "uppercase", letterSpacing: ".5px",
                        whiteSpace: "nowrap", background: "#f8fafc",
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const total     = Number(b.totalAmount || 0);
                    const paid      = Number(b.paidAmount  || 0);
                    const remaining = total - paid;

                    return (
                      <tr key={b._id}
                        style={{ borderBottom: "1px solid #f3f4f6" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 700, color: "#166534" }}>
                          {b.roomUnit?.roomNumber ?? "—"}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 13, color: "#374151" }}>
                          {b.roomType?.name ?? "—"}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 13, color: "#1d4ed8", fontWeight: 600 }}>
                          {fmtDate(b.checkIn)}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 13, color: "#6b7280" }}>
                          {fmtDate(b.checkOut)}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 700, color: "#111827" }}>
                          ฿{fmt(total)}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 700, color: "#16a34a" }}>
                          ฿{fmt(paid)}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 700,
                          color: remaining > 0 ? "#dc2626" : "#16a34a" }}>
                          {remaining > 0 ? `฿${fmt(remaining)}` : t(lang, "paidFull")}
                        </td>
                        <td style={{ padding: "13px 16px" }}>
                          <StatusBadge status={b.status} lang={lang} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;